/**
 * useAuth Hook
 * Custom hook for authentication-related operations
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../utils/authHelpers';
import { MESSAGES, HTTP_STATUS } from '../utils/appConstants';

/**
 * Custom hook for authentication operations with loading and error states
 * @param {Function} apiCall - Authentication API function to call
 * @param {Object} options - Hook options
 * @returns {Object} Authentication state and handlers
 */
export const useAuth = (apiCall, options = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const submittingRef = useRef(false);
  const navigate = options.navigate ? useNavigate() : null;

  /**
   * Execute authentication API call
   */
  const execute = useCallback(async (...args) => {
    // Prevent duplicate submissions
    if (submittingRef.current || loading) {
      console.log('⚠️ Request already in progress, ignoring');
      return { success: false };
    }

    submittingRef.current = true;
    setError('');
    setLoading(true);
    setSuccess(false);

    try {
      const response = await apiCall(...args);

      if (response.ok) {
        // Check if response contains a warning (partial success)
        const hasWarning = response.data?.warning || response.data?.error;

        if (hasWarning) {
          // Partial success - show error but don't call onSuccess
          const warningMessage = response.data.message || response.data.error || MESSAGES.ERROR.GENERIC;
          setError(warningMessage);

          // Call error callback if provided
          if (options.onError) {
            options.onError(warningMessage, response);
          }

          return { success: false, error: warningMessage, warning: true };
        }

        // Full success
        setSuccess(true);

        // Call success callback if provided
        if (options.onSuccess) {
          options.onSuccess(response.data);
        }

        return { success: true, data: response.data };
      } else {
        const errorMessage = getErrorMessage(
          response.status,
          response.data,
          options.errorMessages || {}
        );
        setError(errorMessage);

        // Call error callback if provided
        if (options.onError) {
          options.onError(errorMessage, response);
        }

        return { success: false, error: errorMessage };
      }
    } catch (err) {
      console.error('Authentication error:', err);

      // Better error detection
      let errorMessage;
      if (!navigator.onLine) {
        errorMessage = 'Geen internetverbinding. Controleer je netwerk en probeer opnieuw.';
      } else if (err.message.includes('Failed to fetch') || err.name === 'TypeError') {
        errorMessage = MESSAGES.ERROR.NETWORK;
      } else if (err.name === 'AbortError') {
        errorMessage = 'Verzoek time-out. Probeer het opnieuw.';
      } else {
        errorMessage = MESSAGES.ERROR.GENERIC;
      }

      setError(errorMessage);

      if (options.onError) {
        options.onError(errorMessage, err);
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  }, [apiCall, loading, options]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setError('');
    setSuccess(false);
    setLoading(false);
    submittingRef.current = false;
  }, []);

  /**
   * Clear only error
   */
  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    loading,
    error,
    success,
    execute,
    reset,
    clearError,
    setError,
  };
};

export default useAuth;
